import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format address to short form (0x1234...5678)
 */
export function formatAddress(address: string): string {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

/**
 * Format token amount with decimals
 */
export function formatTokenAmount(amount: bigint, decimals = 18): string {
  const divisor = 10n ** BigInt(decimals)
  const whole = amount / divisor
  const fraction = amount % divisor
  return `${whole}.${fraction.toString().padStart(decimals, '0').slice(0, 4)}`
}

/**
 * Round ETH value for display (avoids long decimals breaking layout).
 * Uses up to maxDecimals, trims trailing zeros.
 */
export function formatEthDisplay(value: string | number, maxDecimals = 6): string {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return '0'
  if (n === 0) return '0'
  const fixed = n.toFixed(maxDecimals)
  return parseFloat(fixed).toString()
}

/**
 * Convert seconds to readable format
 */
export function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)

  return parts.join(' ') || '0m'
}

/**
 * Default IPFS gateway for converting ipfs:// URIs to HTTP URLs.
 * You can override it in .env via VITE_IPFS_GATEWAY, for example:
 *   VITE_IPFS_GATEWAY=https://white-abundant-piranha-255.mypinata.cloud/ipfs/
 *
 * По умолчанию ставим ipfs.io, т.к. он у тебя точно открывает JSON и картинки.
 */
export const IPFS_GATEWAY: string =
  (import.meta as any).env?.VITE_IPFS_GATEWAY || 'https://ipfs.io/ipfs/'

/** Fallback gateways if the default fails (CORS, 404, etc.) */
const IPFS_GATEWAY_FALLBACKS = [
  'https://ipfs.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://dweb.link/ipfs/',
  'https://w3s.link/ipfs/',
  'https://4everland.io/ipfs/',
]

function ipfsPath(uri: string): string {
  if (!uri) return ''
  if (uri.startsWith('ipfs://')) return uri.slice('ipfs://'.length)
  if (!uri.startsWith('http://') && !uri.startsWith('https://')) return uri // raw CID
  return ''
}

/**
 * Convert ipfs:// URIs or raw CIDs to HTTP URLs using the configured gateway.
 * - ipfs://CID/path -> <GATEWAY>/CID/path
 * - CID only        -> <GATEWAY>/CID
 * - http(s) URLs    -> returned as-is
 */
export function ipfsToHttp(uri: string | undefined | null, gateway = IPFS_GATEWAY): string {
  if (!uri) return ''
  if (uri.startsWith('http://') || uri.startsWith('https://')) return uri
  const path = ipfsPath(uri)
  return path ? `${gateway}${path}` : uri
}

/**
 * Fetch JSON from IPFS, trying main gateway then fallbacks.
 * Use for metadata (X.json). Helps when Pinata has CORS/404 issues.
 */
export async function fetchIpfsJson<T = unknown>(ipfsUri: string): Promise<{ data: T; gateway: string }> {
  const path = ipfsPath(ipfsUri)
  if (!path) {
    const res = await fetch(ipfsUri)
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${ipfsUri}`)
    const data = (await res.json()) as T
    return { data, gateway: '' }
  }
  const gateways = [IPFS_GATEWAY, ...IPFS_GATEWAY_FALLBACKS]
  let lastErr: Error | null = null
  for (const g of gateways) {
    try {
      const url = `${g}${path}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as T
      return { data, gateway: g }
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e))
    }
  }
  throw lastErr ?? new Error('Failed to fetch IPFS')
}

/**
 * Build list of HTTP URLs for an IPFS image using main gateway + fallbacks.
 * Used by frontend to retry image loading if one gateway fails.
 */
export function buildIpfsGatewayUrls(ipfsUri: string): string[] {
  if (!ipfsUri) return []
  if (ipfsUri.startsWith('http://') || ipfsUri.startsWith('https://')) return [ipfsUri]

  const path = ipfsPath(ipfsUri)
  if (!path) return []

  const gateways = [IPFS_GATEWAY, ...IPFS_GATEWAY_FALLBACKS]
  return gateways.map((g) => `${g}${path}`)
}
